import axios from 'axios';
import { BackendResponse } from '../types';
import { User } from '../features/auth/types';
import { getConfig } from '../config';

const { BACKEND_URL, API_TIMEOUT } = getConfig();

const api = axios.create({
    baseURL: BACKEND_URL,
    timeout: API_TIMEOUT,
    withCredentials: true,
    headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
    },
});

// Interceptor para adicionar token automaticamente
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('auth_token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Interceptor para tratar erros de autenticação
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('auth_token');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

export const authService = {
    async getCsrfCookie(): Promise<void> {
        await api.get('/sanctum/csrf-cookie');
    },

    async login(credentials: { email: string; password: string }): Promise<{ user: User; token: string }> {
        // Primeiro obtém o cookie CSRF
        await this.getCsrfCookie();
        
        // Depois faz o login
        const response: BackendResponse = await api.post('/login', {
            email: credentials.email,
            password: credentials.password,
        });
        
        if (response.data.data.token) {
            localStorage.setItem('auth_token', response.data.data.token);
        }

        const userData = response.data.data.user;

        return {
            user: {
                id: userData.id,
                firstName: userData.first_name,
                lastName: userData.last_name,
                fullName: `${userData.first_name} ${userData.last_name}`,
                role: userData.role,
                email: userData.email,
                avatar: userData?.avatar
            } as User,
            token: response.data.data.token
        };
    },

    async logout(): Promise<void> {
        try {
            await api.post('/logout');
        } finally {
            localStorage.removeItem('auth_token');
        }
    },

    async getUser(): Promise<User> {
        const response: BackendResponse = await api.get('/user');
        const userData = response.data.data;

        return {
            id: userData.id,
            firstName: userData.first_name,
            lastName: userData.last_name,
            fullName: `${userData.first_name} ${userData.last_name}`,
            role: userData.role,
            email: userData.email,
            avatar: userData?.avatar
        } as User;
    },
};

export default api;